import React, { MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    useFormContext,
    UseFormMethods,
    Validate,
    ValidateResult
} from '../../../Vendor/react-hook-form/dist/index.ie11'
import { PermissionsLanguage } from '../../../../i18n'
import { LOADING_STRING } from '../../../NonReact/constants/Common'
import { DeviceFields } from '../../../NonReact/constants/DeviceConstants'
import { DEVICE_FILTER_EXTENSIONS_ONLY } from '../../../NonReact/constants/DeviceTypes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { RESTAPIError } from '../../Common/API'
import {
    ALL_VALUE,
    getAgentsFromPartition,
    getCMEsAsSelectOptions,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions,
    INVALID_VALUE
} from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'
import { SelectOptions } from '../../Common/Components/Selects'
import { ComboBoxItem, DeviceSelectItem, SelectItem, SelectOption } from '../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { MessageContext, ReactState } from '../../Common/Contexts'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import UserInfoContext from '../../Common/Contexts/UserInfoContext'
import { StateSetter } from '../../Common/Types/Types'
import { handleUnknownForAvailables, removeUnknownOption } from '../AddUserFunctions'
import { AKIXI_CME_NAME, DEFAULT_CME_ID, SERVER_ALL_VALUE, SERVER_NONE_VALUE, SERVER_UNKNOWN_VALUE } from '../Constants'
import {
    AGENTS_PERM_NAME,
    CME_PERM_NAME,
    DEVICES_PERM_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    ENDPOINT_PARTITION_NAME,
    ENDPOINT_TEL_SYS_NAME,
    PARTITION_PERM_NAME,
    REPORTING_LEVEL_NAME,
    ROLE_PERMS_NAMESPACE,
    SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME,
    TEL_SYS_PERM_NAME
} from '../FieldNames'
import { ReportingAccessLevels } from '../Reporting/ReportingAccessLevels'
import { ApplicationUserFormData, FilterValues } from '../Types'
import { PermissionsDefaults } from './PermissionsDefaults'
import { AgentFields } from '../../../NonReact/constants/AgentConstants'
import { indicateUnmonitoredItems } from '../../Common/Utils/ItemsResponseUtils'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { INTEGRATION_TYPE, IntegrationType } from '../../TelSysConfiguration/Constants/Constants'
import { AdminUserResponse } from '../../../NonReact/common/Types/Types'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { getBusinessAwareValidationMessage } from '../Rules/UserLicenceValidationMessages'

export type FormPermissions = ApplicationUserFormData['permissions']

type Props = {
    scope?: FormPermissions
    filters?: FilterValues
    permissionAvailableDevice: ReactState<SelectItem[]>
    permissionAvailableAgents: ReactState<SelectItem[]>
    setUserdetailsFromDevice: (device: string) => void
    showSyncPermissionsCheckBox: boolean
    viewOnly?: boolean
    telsysType?: Nullable<TelSysTypes>
    integrationType?: Nullable<IntegrationType>
    setAttachedDevice: StateSetter<Nullable<DeviceSelectItem>>
    isWebexAgentOrSupervisorUser: boolean
}

type DefaultAvailables = [SelectOptions<string>, SelectOptions<number>, SelectOptions<number>]

/**
 * Get the available entities defaults.
 *
 * @param scope - Form permissions from the server.
 *
 * @returns - An array of defaults.
 */
const getAvailableEntitiesDefaults = (scope?: FormPermissions, filters?: FilterValues): DefaultAvailables => [
    [
        {
            label:
                scope?.cmenvironment || filters?.cmeID
                    ? scope?.cmenvironment ?? filters?.cmeID ?? AKIXI_CME_NAME
                    : AKIXI_CME_NAME,
            value: scope?.cmenvironment ?? filters?.cmeID ?? DEFAULT_CME_ID
        }
    ],
    [{ label: LOADING_STRING, value: scope?.telephonyserver ?? filters?.telSysID ?? ALL_VALUE }],
    [
        {
            label: LOADING_STRING,
            value:
                (scope?.partition && scope.partition.length > 0 ? scope.partition[0] : filters?.partitionID) ??
                ALL_VALUE
        }
    ]
]

/**
 * Retrieves the device fields based on the specified telephony system type.
 *
 * @param {TelSysTypes} telsysTypeID - The ID representing the telephony system type.
 * @returns {DeviceFields[]} - An array of device fields relevant to the given telephony system type.
 *
 * The function dynamically determines which device fields are applicable based on the provided
 * telephony system type. For example:
 * - If the telephony system type is `MS_TEAMS`, the `GROUP_MEMBERSHIP` field is included.
 * - If the telephony system type is `WEBEX_CALLING`, the `PRIMARY_EMAIL` field is included.
 */
export const getDeviceFields = (telsysTypeID: TelSysTypes): DeviceFields[] => [
    ...(telsysTypeID === TelSysTypes.MS_TEAMS ? [DeviceFields.GROUP_MEMBERSHIP] : []),
    ...(telsysTypeID === TelSysTypes.WEBEX_CALLING ? [DeviceFields.PRIMARY_EMAIL] : [])
]

/**
 * Permissions for the user details.
 *
 * @param scope - The permissions as a form structure.
 * @param permissionsAvailableDevice - Available devices for permissions scope.
 * @param permissionAvailableAgents - Available agents for permissions scope.
 * @param setUserdetailsFromDevice - Set user details from device.
 * @param showSyncPermissionsCheckBox - Show sync permissions.
 * @param viewOnly - View only.
 */
const Permissions: React.FC<Props> = ({
    scope,
    filters,
    permissionAvailableDevice: [availableDevices, setAvailableDevices],
    permissionAvailableAgents: [availableAgents, setAvailableAgents],
    setUserdetailsFromDevice,
    showSyncPermissionsCheckBox,
    viewOnly,
    telsysType,
    integrationType,
    setAttachedDevice,
    isWebexAgentOrSupervisorUser
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(PermissionsLanguage.NAME)

    // eslint-disable-next-line
    const {
        Scope: { TelSysID: currentUserTelSys, PartIDInTelSys: currentUserPartition }
    }: AdminUserResponse = useContext(UserInfoContext) as AdminUserResponse

    const isScopedPermissionsTelSys: boolean = currentUserTelSys?.length === 1 && currentUserTelSys[0] !== ALL_VALUE
    const isScopedPermissionsPartition: boolean =
        currentUserPartition?.length === 1 && currentUserPartition[0] !== ALL_VALUE

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const permissionsDefaults: PermissionsDefaults = PermissionsDefaults.getInstance(language)

    const { displayError }: MessageContextType = useContext(MessageContext)
    const { watch, setValue }: UseFormMethods = useFormContext()

    const [cmeDefaults, telSysDefaults, partitionDefaults]: DefaultAvailables = getAvailableEntitiesDefaults(
        scope,
        filters
    )

    const [availableCMEs, setAvailableCMEs]: ReactState<SelectOptions<string>> =
        useState<SelectOptions<string>>(cmeDefaults)
    const [allAvailableTelServers, setAllAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(telSysDefaults)
    const [availableTelServers, setAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(allAvailableTelServers)
    const [allAvailablePartitions, setAllAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(partitionDefaults)
    const [availablePartitions, setAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(allAvailablePartitions)

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingDevices, setIsLoadingDevices]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingAgents, setIsLoadingAgents]: ReactState<boolean> = useState<boolean>(false)

    const isLoading: boolean = isLoadingTelServers || isLoadingPartitions || isLoadingDevices || isLoadingAgents

    const cmeValue: string = watch(CME_PERM_NAME)
    const telSysValue: number = watch(TEL_SYS_PERM_NAME)
    const partitionValue: number[] = watch(PARTITION_PERM_NAME)
    const agentsValue: string = watch(AGENTS_PERM_NAME)
    const devicesValue: string = watch(DEVICES_PERM_NAME)
    const synchPermissions: boolean = watch(SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME)

    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)

    const isEssentialsUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.ESSENTIALS,
        [reportingLevelValue]
    )
    const isProfessionalUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.PROFESSIONAL,
        [reportingLevelValue]
    )
    const isAgentUser: boolean = useMemo(
        (): boolean => reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT,
        [reportingLevelValue]
    )

    // A Business Real-Time User (Professional on an MS Teams Business telsys) is scoped to a device, unlike a
    // Legacy Professional user (partition scope), so the device permission field stays enabled and required.
    const isBusinessRealTimeUser: boolean = useMemo(
        (): boolean =>
            telsysType === TelSysTypes.MS_TEAMS &&
            integrationType === INTEGRATION_TYPE.BUSINESS &&
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL,
        [telsysType, integrationType, reportingLevelValue]
    )

    const isEnterpriseParitionUser: boolean = useMemo(
        (): boolean =>
            reportingLevelValue === ReportingAccessLevels.ESSENTIALS ||
            reportingLevelValue === ReportingAccessLevels.PROFESSIONAL ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT ||
            reportingLevelValue === ReportingAccessLevels.ENTERPRISE ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
        [reportingLevelValue]
    )

    // Create an error handler for an individual loader.
    const handleError =
        (...loadingSetters: StateSetter<boolean>[]): ((e: RESTAPIError) => void) =>
        (e: RESTAPIError): void => {
            loadingSetters.forEach((loadingSetter: StateSetter<boolean>): void => {
                if (isMounted.current) {
                    loadingSetter(false)
                }
            })
            displayError(e)
        }

    useEffect((): void => {
        if (isMounted.current) {
            if (isEnterpriseParitionUser) {
                setAvailablePartitions(
                    allAvailablePartitions.filter(
                        (ap: SelectOption<number>): boolean =>
                            ap.licencedForRptType === ReportingAccessLevels.ENTERPRISE
                    )
                )
            } else {
                setAvailablePartitions(allAvailablePartitions)
            }
        }
    }, [isEnterpriseParitionUser, allAvailablePartitions])

    useEffect((): void => {
        // What to do if there is a scope from the selectedUser
        if (scope) {
            const { cmenvironment, telephonyserver, partition }: FormPermissions = scope

            if (cmenvironment && cmenvironment !== SERVER_UNKNOWN_VALUE) {
                setIsLoadingTelServers(true)
                getTelServersAsSelectOptions(cmenvironment, false, false)
                    .then((telServers: SelectOptions<number>): void => {
                        if (isMounted.current) {
                            setIsLoadingTelServers(false)
                            handleUnknownForAvailables(telServers, setAllAvailableTelServers, telSysValue)
                        }
                    })
                    .catch(handleError(setIsLoadingTelServers))
            }

            if (cmenvironment === SERVER_UNKNOWN_VALUE && telephonyserver === INVALID_VALUE) {
                handleUnknownForAvailables([], setAllAvailableTelServers, telSysValue)
            }

            if (telephonyserver && telephonyserver !== ALL_VALUE && telephonyserver !== INVALID_VALUE) {
                setIsLoadingPartitions(true)
                getPartitionsForTelServer(telephonyserver)
                    .then((partitions: SelectOptions<number>): void => {
                        if (isMounted.current) {
                            setIsLoadingPartitions(false)
                            handleUnknownForAvailables(partitions, setAllAvailablePartitions, partitionValue)
                        }
                    })
                    .catch(handleError(setIsLoadingPartitions))
            }

            if (telephonyserver === INVALID_VALUE && partition?.includes(INVALID_VALUE)) {
                handleUnknownForAvailables([], setAllAvailablePartitions, partitionValue)
            }

            if (
                telephonyserver &&
                telephonyserver !== ALL_VALUE &&
                telephonyserver !== INVALID_VALUE &&
                partition &&
                partition.length === 1 &&
                partition[0] !== ALL_VALUE &&
                partition[0] !== INVALID_VALUE
            ) {
                setIsLoadingDevices(true)
                setIsLoadingAgents(true)
                getDevicesFromPartition(
                    telephonyserver,
                    partition[0],
                    true,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    ...getDeviceFields(telsysType as TelSysTypes)
                )
                    .then(([devices]: [SelectItem[], Definable<number>]): void => {
                        if (isMounted.current) {
                            setIsLoadingDevices(false)
                            setAvailableDevices(devices)
                        }
                    })
                    .catch(handleError(setIsLoadingDevices))
                getAgentsFromPartition(telephonyserver, partition[0], true)
                    .then((agents: SelectItem[]): void => {
                        if (isMounted.current) {
                            setIsLoadingAgents(false)
                            setAvailableAgents(agents)
                        }
                    })
                    .catch(handleError(setIsLoadingAgents))
            }
        }
    }, [scope])

    useEffect((): void => {
        if (isMounted.current) {
            if (isEnterpriseParitionUser) {
                setAvailableTelServers(
                    allAvailableTelServers.filter(
                        (availableTelServer: SelectOption<number>): boolean =>
                            !!availableTelServer.licences?.includes(ReportingAccessLevels.ENTERPRISE)
                    )
                )
            } else {
                setAvailableTelServers(allAvailableTelServers)
            }
        }
    }, [allAvailableTelServers, isEnterpriseParitionUser])

    useEffect((): void => {
        getCMEsAsSelectOptions()
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) {
                    handleUnknownForAvailables(cmes, setAvailableCMEs, cmeValue)
                }
            })
            .catch(displayError)
    }, [])

    useEffect((): void => {
        // What to do when the CME value updates.
        const loadTelSysWithChildCmes: boolean = filters?.includeCmeChildren ?? false

        if (!IsStringEmpty(cmeValue) && cmeValue !== SERVER_UNKNOWN_VALUE) {
            setIsLoadingTelServers(true)
            setAllAvailableTelServers(telSysDefaults)
            getTelServersAsSelectOptions(cmeValue, loadTelSysWithChildCmes, false)
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                        handleUnknownForAvailables(telServers, setAllAvailableTelServers, telSysValue)
                    }
                })
                .catch(handleError(setIsLoadingTelServers))
        } else {
            handleUnknownForAvailables([], setAllAvailableTelServers, telSysValue)
            handleUnknownForAvailables([], setAllAvailablePartitions, partitionValue)
            handleUnknownForAvailables([], setAvailableDevices, devicesValue)
            handleUnknownForAvailables([], setAvailableAgents, agentsValue)
        }

        removeUnknownOption(setAvailableCMEs, cmeValue)
    }, [cmeValue, filters?.includeCmeChildren])

    useEffect((): void => {
        // What to do when the telephony server value changes.
        if (!IsVarEmpty(telSysValue)) {
            if (telSysValue !== ALL_VALUE && telSysValue !== INVALID_VALUE) {
                setIsLoadingPartitions(true)
                getPartitionsForTelServer(telSysValue)
                    .then((partitions: SelectOptions<number>): void => {
                        if (isMounted.current) {
                            setIsLoadingPartitions(false)
                            handleUnknownForAvailables(partitions, setAllAvailablePartitions, partitionValue)
                        }
                    })
                    .catch(handleError(setIsLoadingPartitions))
            }

            handleUnknownForAvailables([], setAllAvailablePartitions, partitionValue)
            handleUnknownForAvailables([], setAvailableDevices, devicesValue)
            handleUnknownForAvailables([], setAvailableAgents, agentsValue)
        }

        removeUnknownOption(setAllAvailableTelServers, telSysValue)
    }, [telSysValue])

    useEffect((): void => {
        // What to do when the partition value changes.
        if (!IsVarEmpty(partitionValue) && partitionValue.length > 0) {
            const partVal: number = partitionValue[0]

            if (
                telSysValue !== ALL_VALUE &&
                telSysValue !== INVALID_VALUE &&
                partitionValue.length === 1 &&
                partVal !== ALL_VALUE &&
                partVal !== INVALID_VALUE
            ) {
                setIsLoadingDevices(true)
                setIsLoadingAgents(true)
                indicateUnmonitoredItems(
                    getDevicesFromPartition(
                        telSysValue,
                        partVal,
                        false,
                        isEssentialsUser ? DEVICE_FILTER_EXTENSIONS_ONLY : undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        DeviceFields.TELSYS_USERNAME,
                        DeviceFields.OVERRIDE_MONITORING,
                        ...getDeviceFields(telsysType as TelSysTypes)
                    ).then(([devices]: [DeviceSelectItem[], Definable<number>]): DeviceSelectItem[] => devices)
                )
                    .then((devices: SelectItem[]): void => {
                        if (isMounted.current) {
                            setIsLoadingDevices(false)
                            handleUnknownForAvailables(devices, setAvailableDevices, devicesValue)

                            if (
                                isEssentialsUser &&
                                !devices.some((dev: SelectItem): boolean => dev.value === devicesValue)
                            ) {
                                setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                            }
                        }
                    })
                    .catch(handleError(setIsLoadingDevices))
                indicateUnmonitoredItems(
                    getAgentsFromPartition(
                        telSysValue,
                        partVal,
                        false,
                        undefined,
                        undefined,
                        AgentFields.ID_ON_TEL_SYS,
                        AgentFields.OVERRIDE_MONITORING
                    )
                )
                    .then((agents: SelectItem[]): void => {
                        if (isMounted.current) {
                            setIsLoadingAgents(false)
                            handleUnknownForAvailables(agents, setAvailableAgents, agentsValue)
                        }
                    })
                    .catch(handleError(setIsLoadingAgents))
            } else {
                handleUnknownForAvailables([], setAvailableDevices, devicesValue)
                handleUnknownForAvailables([], setAvailableAgents, agentsValue)
            }
        }

        removeUnknownOption(setAllAvailablePartitions, partitionValue)

        // The stringify is needed as array values don't get evaluated properly.
    }, [telSysValue, JSON.stringify(partitionValue), isEssentialsUser, telsysType])

    useEffect((): void => {
        removeUnknownOption(setAvailableDevices, devicesValue)

        setAttachedDevice(
            availableDevices.find((device: DeviceSelectItem): boolean => device.value === devicesValue) ?? undefined
        )
    }, [devicesValue])

    useEffect((): void => {
        removeUnknownOption(setAvailableAgents, agentsValue)
    }, [agentsValue])

    const hasAnyAdminPermission = (): boolean => {
        const permissions: Record<string, Record<string, boolean>> = watch(ROLE_PERMS_NAMESPACE)

        return permissions
            ? Object.values(permissions).some((permission: Record<string, boolean>): boolean =>
                  Object.values(permission).some((permissionBool: boolean): boolean => permissionBool)
              )
            : false
    }

    const isAdminRules = (message: string): Record<string, Validate> => ({
        isAdmin: (value: string): ValidateResult => {
            if (value !== SERVER_ALL_VALUE && hasAnyAdminPermission()) {
                return message
            }

            return undefined
        }
    })

    // A Business Manager (Call Centre Supervisor on an MS Teams Business telsys) may only hold administrator
    // permissions when scoped to all partitions (telephony system level). Mirrors backend error 11254.
    const businessManagerPartitionRule = (message: string): Record<string, Validate> => ({
        businessManagerTelSysScope: (value: number[]): ValidateResult => {
            const isBusinessManager: boolean =
                telsysType === TelSysTypes.MS_TEAMS &&
                integrationType === INTEGRATION_TYPE.BUSINESS &&
                watch(REPORTING_LEVEL_NAME) === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR

            if (!isBusinessManager || !hasAnyAdminPermission()) return undefined

            const isAllPartitions: boolean =
                IsVarEmpty(value) || value.length === 0 || (value.length === 1 && value[0] === ALL_VALUE)

            if (isAllPartitions) return undefined

            return message
        }
    })

    return (
        <div className='permissions-tab tab-area'>
            <FormRow className='central-management-environment'>
                <OutlinedSelect
                    disabled={isScopedPermissionsTelSys || viewOnly}
                    name={CME_PERM_NAME}
                    label={language.get(PermissionsLanguage.PermissionsLanguage_CmEnvironment)}
                    options={availableCMEs}
                    noChange={isLoading}
                    handleChange={(): void => {
                        setValue(ENDPOINT_TEL_SYS_NAME, ALL_VALUE)
                        setValue(ENDPOINT_PARTITION_NAME, ALL_VALUE)
                        setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                        setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)

                        setValue(TEL_SYS_PERM_NAME, ALL_VALUE)
                        setValue(PARTITION_PERM_NAME, [ALL_VALUE])
                        setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                        setValue(AGENTS_PERM_NAME, SERVER_ALL_VALUE)
                    }}
                />
            </FormRow>
            <FormRow className='telephony-server'>
                <ComboBox
                    variant='outlined'
                    disabled={isScopedPermissionsTelSys || viewOnly}
                    name={TEL_SYS_PERM_NAME}
                    label={language.get(PermissionsLanguage.PermissionsLanguage_TelServer)}
                    defaultOption={
                        isScopedPermissionsTelSys
                            ? availableTelServers[0] ?? permissionsDefaults.getAllTelServersOption()
                            : permissionsDefaults.getAllTelServersOption()
                    }
                    options={isScopedPermissionsTelSys ? [] : availableTelServers}
                    loading={isLoadingTelServers}
                    noChange={isLoading}
                    onChange={(telServer: Nullable<ComboBoxItem<number>>): void => {
                        if (!isEssentialsUser) {
                            setValue(ENDPOINT_TEL_SYS_NAME, (telServer as ComboBoxItem<number>).value)
                            setValue(ENDPOINT_PARTITION_NAME, ALL_VALUE)
                            setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                            setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
                        }

                        setValue(PARTITION_PERM_NAME, [ALL_VALUE])
                        setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                        setValue(AGENTS_PERM_NAME, SERVER_ALL_VALUE)
                    }}
                />
            </FormRow>
            <FormRow className='partition'>
                <ValidationErrors name={PARTITION_PERM_NAME}>
                    <ComboBox
                        variant='outlined'
                        disabled={isScopedPermissionsPartition || viewOnly}
                        name={PARTITION_PERM_NAME}
                        label={language.get(PermissionsLanguage.PermissionsLanguage_Partition)}
                        multiple
                        defaultOption={
                            isScopedPermissionsPartition
                                ? availablePartitions[0] ?? permissionsDefaults.getAnyPartitionOption()
                                : permissionsDefaults.getAnyPartitionOption()
                        }
                        options={isScopedPermissionsPartition ? [] : availablePartitions}
                        loading={isLoadingPartitions}
                        noChange={isLoading}
                        onChange={(partitionIDs: Nullable<ComboBoxItem<number>[]>): void => {
                            const partIDs: Nullable<ComboBoxItem<number>[]> = partitionIDs

                            if (!isEssentialsUser) {
                                if (partIDs && partIDs?.length === 1) {
                                    setValue(ENDPOINT_PARTITION_NAME, partIDs[0].value)
                                } else {
                                    setValue(ENDPOINT_PARTITION_NAME, ALL_VALUE)
                                }

                                setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                                setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
                            }

                            setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                            setValue(AGENTS_PERM_NAME, SERVER_ALL_VALUE)
                        }}
                        rules={{
                            validate: businessManagerPartitionRule(
                                language.get(PermissionsLanguage.BusinessManagerPartitionValidation)
                            )
                        }}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow className='device'>
                <ValidationErrors name={DEVICES_PERM_NAME}>
                    <ComboBox
                        variant='outlined'
                        name={DEVICES_PERM_NAME}
                        label={language.get(PermissionsLanguage.PermissionsLanguage_Device)}
                        defaultOption={permissionsDefaults.getAnyDevice(agentsValue)}
                        options={availableDevices}
                        loading={isLoadingDevices}
                        noChange={isLoading}
                        onChange={(newValue: Nullable<ComboBoxItem<string>>): void => {
                            if (!isEssentialsUser) {
                                setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                            }

                            if (
                                newValue &&
                                (isEssentialsUser || isBusinessRealTimeUser || isWebexAgentOrSupervisorUser)
                            ) {
                                setUserdetailsFromDevice(newValue.value)
                            }
                        }}
                        rules={{
                            validate: {
                                ...isAdminRules(language.get(PermissionsLanguage.DeviceValidation)),
                                isEssentials: (value: string): ValidateResult => {
                                    const rptLevelValue: string = watch(REPORTING_LEVEL_NAME)
                                    const isEssUser: boolean = rptLevelValue === ReportingAccessLevels.ESSENTIALS

                                    if (value === SERVER_ALL_VALUE && isEssUser) {
                                        return getBusinessAwareValidationMessage(
                                            language,
                                            PermissionsLanguage.EssentialsDeviceValidation,
                                            telsysType,
                                            integrationType
                                        )
                                    }

                                    return undefined
                                },
                                isBusinessRealTime: (value: string): ValidateResult => {
                                    // Read the level from the form (not the render-captured memo) so this
                                    // matches when validation runs in the same tick as an Agent->Real-Time
                                    // switch, before this component re-renders — keeping the device indicator
                                    // in step with the Endpoint tab, which already validates against the live level.
                                    const rptLevelValue: string = watch(REPORTING_LEVEL_NAME)
                                    const isBusinessRealTimeLevel: boolean =
                                        telsysType === TelSysTypes.MS_TEAMS &&
                                        integrationType === INTEGRATION_TYPE.BUSINESS &&
                                        rptLevelValue === ReportingAccessLevels.PROFESSIONAL

                                    if (value === SERVER_ALL_VALUE && isBusinessRealTimeLevel) {
                                        return getBusinessAwareValidationMessage(
                                            language,
                                            PermissionsLanguage.ProfessionalDeviceValidation,
                                            telsysType,
                                            integrationType
                                        )
                                    }

                                    return undefined
                                }
                            }
                        }}
                        disabled={
                            viewOnly ||
                            (isProfessionalUser && !isBusinessRealTimeUser) ||
                            isAgentUser ||
                            synchPermissions
                        }
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow className='agent'>
                <ValidationErrors name={AGENTS_PERM_NAME}>
                    <ComboBox
                        variant='outlined'
                        name={AGENTS_PERM_NAME}
                        label={language.get(PermissionsLanguage.PermissionsLanguage_AndOrAgent)}
                        defaultOption={permissionsDefaults.getAnyAgent(devicesValue)}
                        options={availableAgents}
                        loading={isLoadingAgents}
                        noChange={isLoading}
                        onChange={(newValue: Nullable<ComboBoxItem<string>>): void => {
                            if (!isEssentialsUser) {
                                setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)
                            }

                            if (newValue && isWebexAgentOrSupervisorUser) {
                                setUserdetailsFromDevice(newValue.value)
                            }
                        }}
                        rules={{ validate: isAdminRules(language.get(PermissionsLanguage.AgentValidation)) }}
                        disabled={viewOnly || isEssentialsUser || isProfessionalUser || isAgentUser || synchPermissions}
                    />
                </ValidationErrors>
            </FormRow>

            {showSyncPermissionsCheckBox ? (
                <FormRow className='synchronise-supervisor-permissions'>
                    <RouterCheckbox
                        labelLeft={language.get(
                            PermissionsLanguage.PermissionsLanguage_SynchroniseSupervisorPermissions
                        )}
                        name={SYNCHRONISE_SUPERVISOR_PERMISSIONS_NAME}
                        disabled={viewOnly}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}

export default Permissions
